import type {
  InvestorNetWorthChange,
  NetWorthChangeDirection,
  NetWorthChangeTotals,
} from '@/interfaces/netWorthChanges';

/** How many clients the widget ranks. */
export const NET_WORTH_CHANGES_LIMIT = 10;

/**
 * Whether a row's percentage has no meaning: a client who started the period
 * at zero has no base to measure the change against. The API still reports
 * 100 for these, so the percentage must be hidden rather than shown.
 */
export function hasUndefinedPercentChange(
  row: InvestorNetWorthChange,
): boolean {
  return row.OldDollarBalance === 0;
}

function matchesDirection(
  row: InvestorNetWorthChange,
  direction: NetWorthChangeDirection,
): boolean {
  if (direction === 'gainers') return row.DollarChange >= 0;
  if (direction === 'losers') return row.DollarChange < 0;
  return true;
}

function byDollarMagnitude(
  a: InvestorNetWorthChange,
  b: InvestorNetWorthChange,
): number {
  return Math.abs(b.DollarChange) - Math.abs(a.DollarChange);
}

/**
 * Larger moves first, whichever way they went, so the biggest gains and the
 * biggest falls both reach the top. Ties go to the larger dollar move.
 *
 * A client who started at zero has no percentage, only the API's stand-in
 * 100, so they rank after every real percentage and among themselves by
 * dollars. Ranking the stand-in would let $0 to $1 outrank a genuine 99%.
 */
function byMagnitude(
  a: InvestorNetWorthChange,
  b: InvestorNetWorthChange,
): number {
  const aUndefined = hasUndefinedPercentChange(a);
  const bUndefined = hasUndefinedPercentChange(b);
  if (aUndefined !== bUndefined) return aUndefined ? 1 : -1;
  if (aUndefined) return byDollarMagnitude(a, b);

  const byPercent = Math.abs(b.PercentChange) - Math.abs(a.PercentChange);
  if (byPercent !== 0) return byPercent;
  return byDollarMagnitude(a, b);
}

/**
 * Picks the clients the widget shows: those moving in `direction`, ranked by
 * the size of their percentage change, capped at `limit`.
 *
 * The cap is applied after the direction filter, so Losers shows the ten
 * biggest falls rather than whichever falls happened to make an overall ten.
 * Display order is left to the grid.
 */
export function rankNetWorthChanges(
  records: readonly InvestorNetWorthChange[],
  direction: NetWorthChangeDirection = 'all',
  limit: number = NET_WORTH_CHANGES_LIMIT,
): InvestorNetWorthChange[] {
  return records
    .filter((row) => matchesDirection(row, direction))
    .sort(byMagnitude)
    .slice(0, limit);
}

/**
 * Narrows an already-ranked list to names containing `term`, ignoring case.
 * Searching never re-ranks or reaches past the ranked set, so the clients it
 * returns are always ones the ranking already chose.
 */
export function searchNetWorthChanges(
  ranked: readonly InvestorNetWorthChange[],
  term: string,
): InvestorNetWorthChange[] {
  const needle = term.trim().toLocaleLowerCase();
  if (!needle) return [...ranked];
  return ranked.filter((row) => row.Name.toLocaleLowerCase().includes(needle));
}

/**
 * Sums the rows for the footer. The percentage is recomputed from the sums
 * rather than averaged, so it is the change of the group as a whole -- the
 * same figure legacy's footer shows.
 */
export function totalNetWorthChanges(
  rows: readonly InvestorNetWorthChange[],
): NetWorthChangeTotals {
  const totals = rows.reduce(
    (sum, row) => ({
      OldDollarBalance: sum.OldDollarBalance + row.OldDollarBalance,
      DollarBalance: sum.DollarBalance + row.DollarBalance,
      DollarChange: sum.DollarChange + row.DollarChange,
    }),
    { OldDollarBalance: 0, DollarBalance: 0, DollarChange: 0 },
  );

  if (totals.OldDollarBalance === 0) {
    return { ...totals, PercentChange: null };
  }

  return {
    ...totals,
    PercentChange:
      (totals.DollarChange / Math.abs(totals.OldDollarBalance)) * 100,
  };
}
